import _ from 'lodash';
import React from 'react';
import MockServiceLogs from 'views/MockServices/Tabs/MockServices/MockServiceLogs';
import { MockService, SV } from 'types/rest/mockServiceTypes';
import { connect } from 'react-redux';
import {
  createDraft,
  discardLocalChanges,
  mockServiceChanged,
  mockServicePatchAction,
  mockServiceTransactionContentChanged,
  mockServiceTransactionsChanged,
} from 'store/mockServices/mockServiceList/mockServiceListActions';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { ApplicationState } from 'store/rootReducer';
import { isOplLocation } from 'utils/mockServiceUtils';
import { DetailTabKey } from 'store/mockServices/mockServiceTypes';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import {
  getAssetsForMockService,
  mockServiceDetailTabChanged,
  mockServiceParameterTabChanged,
  toggleShowMore,
  updateMockServiceAssets,
} from 'store/mockServices/mockServiceDetail/mockServiceDetailActions';
import { getPublishConfig } from 'utils/blazeDataUtils';
import { IterationOverrides } from 'types/rest/blazeDataTypes';
import { dateAndAuthorFormat, getDateWithFormat } from 'utils/dateUtil';
import MarEolAlert from 'components/alerts/MarEolAlert';
import RadioButton from 'components/buttons/RadioButton';
import ConfigurationsDropDown from '../../../MockServicesFilter/ConfigurationsDropDown';
import headerCellStyles from '../Cells/HeaderCell.module.css';
import styles from './MockServiceDetail.module.css';
import HelpTooltip from 'components/overlays/helpTooltip/HelpTooltip';
import BzmIcon from 'components/icons/BzmIcon';
import clsx from 'clsx';
import MixpanelService from '../../../../../utils/mixpanelService';
import {
  MOCK_SERVICE_APPLY_CONFIGURATION,
  MOCK_SERVICE_APPLY_SECRET_VAULT,
  MOCK_SERVICE_SHOW_MORE,
} from 'utils/mixpanelEvents';
import { RuntimeConfigState } from '../RuntimeConfigTab';
import DeploymentInfo from '../DeploymentInfo';
import { MockParameterState } from '../MockParameters';
import MockServiceDetailTabs from './MockServiceDetailTabs';
import { isFeatureEnabled } from '../../../../../utils/bzmUserConfigUtil';
import SecretsVaultDropdown, { getSelectedItem } from '../SecretsVaultDropdown';

interface LocalProps {
  rowData: MockService;
  saveServiceHandler: (mockService: MockService, updateTemplate?: boolean) => void;
  disabled: boolean;
}

type MockServiceDetailProps = StateProps & DispatchProps & LocalProps & WithTranslation;

interface LocalState {
  isModalWithLogsShown: boolean;
  activeTab: string;
  description: string;
  updateTemplate: boolean;
  appliedTemplateName: string;
  configurationId: number | null;
  secretsVaultId: number | null;
}

class MockServiceDetail extends React.Component<MockServiceDetailProps> {
  state: LocalState = {
    isModalWithLogsShown: false,
    activeTab: 'main',
    description: this.props.rowData.description,
    updateTemplate: false,
    appliedTemplateName: '',
    configurationId: this.props.rowData.configurationId,
    secretsVaultId: this.props.rowData.secretsVaultId,
  };

  componentDidMount() {
    //Load assets only if they are empty...
    if (_.isNil(this.props.assetBundle) || _.isNil(this.props.assetBundle.assets)) {
      this.props.getAssetsForMockService(this.props.rowData);
    }
    if (
      getSelectedItem(this.state.secretsVaultId, this.props.secretsVaultList).id === '-1' &&
      this.props.showMore !== true
    ) {
      this.props.toggleShowMore();
    }
  }

  openLogModalHandler = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.setState({ isModalWithLogsShown: true });
  };

  closeLogsModalHandler = () => {
    this.setState({ isModalWithLogsShown: false });
  };

  afterSaveCallback = () => {
    //default functionality is nothing.  This is overridden in TransactionSimpleDetail.tsx
  };

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<MockServiceDetailProps>): void {
    if (nextProps.rowData.description !== this.state.description) {
      this.setState({ description: nextProps.rowData.description });
    }
    if (nextProps.rowData.configurationId !== this.state.configurationId) {
      this.setState({ configurationId: nextProps.rowData.configurationId });
    }
    if (nextProps.rowData.secretsVaultId !== this.state.secretsVaultId) {
      this.setState({ secretsVaultId: nextProps.rowData.secretsVaultId });
    }
  }

  saveOrUpdateMockService = () => {
    const mockService = _.cloneDeep(this.props.rowData);
    if (mockService.thinkTime && _.isEmpty(mockService.thinkTime.toString())) {
      mockService.thinkTime = 0;
    }
    this.props.saveServiceHandler(mockService, this.state.updateTemplate);
    this.afterSaveCallback();
  };

  /**
   * Updates description in local state.
   * @param description
   */
  updateLocalDescription = (description: string) => {
    this.setState({ description: description });
    this.updateDescriptionDebounced(description);
  };

  /**
   * Updates configurationId in local state.
   * @param id
   */
  updateConfigurationId = (id: number) => {
    const configId = String(id) !== String(-1) ? id : null;
    this.setState({ configurationId: configId });
    this.updateConfigurationIdDebounced(configId);
  };

  /**
   * Updates secrets Vault Id in local state.
   * @param id
   */
  updateSecretsVaultId = (id: number) => {
    const secId = String(id) !== String(-1) ? id : null;
    this.setState({ secretsVaultId: secId });
    this.updateSecretsVaultIdDebounced(secId);
  };

  /**
   * Debounce call for update of the description outside.
   */
  updateDescriptionDebounced = _.debounce((description: string) => {
    const mockService = _.cloneDeep(this.props.rowData);
    mockService.description = description;
    this.props.mockServicePatchAction(mockService);
  }, 600);

  /**
   * Debounce call for update of the configurationId outside.
   */
  updateConfigurationIdDebounced = _.debounce((id: number | null) => {
    const mockService = _.cloneDeep(this.props.rowData);
    mockService.configurationId = id;
    this.props.mockServicePatchAction(mockService);
    MixpanelService.track(MOCK_SERVICE_APPLY_CONFIGURATION());
  }, 600);

  /**
   * Debounce call for update of the secrets Vault Id outside.
   */
  updateSecretsVaultIdDebounced = _.debounce((id: number | null) => {
    const mockService = _.cloneDeep(this.props.rowData);
    mockService.secretsVaultId = id;
    this.props.mockServicePatchAction(mockService);
    MixpanelService.track(MOCK_SERVICE_APPLY_SECRET_VAULT());
  }, 600);

  setExtendedLogging = (loggingEnabled: boolean) => {
    const mockService = _.cloneDeep(this.props.rowData);
    mockService.loggingEnabled = loggingEnabled;
    this.props.mockServicePatchAction(mockService);
  };

  getActionButtons = () => {
    const { t } = this.props;
    const saveUpdateButtonLabel = !this.props.rowData.metaData.isDeployed
      ? t('mockServiceDetails.save')
      : t('mockServiceDetails.update');

    const saveUpdateButtonTitle = !this.props.rowData.metaData.isDeployed
      ? t('mockServiceDetails.saveMockService')
      : t('mockServiceDetails.updateMockService');

    const isInProgress = this.props.rowData.metaData.inProgress;

    return (
      <div className="text-right" style={{ marginTop: '17px' }}>
        {/* Discard Local Changes button */}
        {!isInProgress && this.props.rowData.isDraft && this.props.rowData.serviceMockId && (
          <Button
            disabled={this.props.rowData.type === 'SV'}
            variant={'link'}
            onClick={() => this.props.discardLocalChanges(this.props.rowData)}
          >
            Discard Local Changes
          </Button>
        )}
        {/* ./ Discard Local Changes button */}

        {/* Save virtual service Btn */}
        <button
          disabled={
            isInProgress ||
            this.props.disabled ||
            (this.props.rowData.isDraft !== true && !this.props.rowData.metaData.isDeployed)
          }
          id={`mockServiceDetailSaveButton` + this.props.rowData.id}
          title={saveUpdateButtonTitle}
          className={`btn btn-primary`}
          onClick={this.saveOrUpdateMockService}
        >
          {saveUpdateButtonLabel}
        </button>
        {/* Save virtual service Btn */}
      </div>
    );
  };

  updateParameters = (params: MockParameterState) => {
    const ms = Object.assign({}, this.props.rowData, params);
    this.props.mockServicePatchAction(ms);
  };

  updateParametersDebounced = _.debounce((params: MockParameterState) => {
    this.updateParameters(params);
  }, 600);

  updateRuntimeConfig = _.debounce((params: RuntimeConfigState) => {
    const ms = Object.assign({}, this.props.rowData, params);
    this.props.mockServicePatchAction(ms, 'runtimeConfig');
  }, 600);

  /**
   * Changes active tab.
   * @param activeTab
   */
  changeActiveTabHandler = (activeTab: DetailTabKey) => {
    this.props.mockServiceDetailTabChanged(activeTab);
  };

  updateIterationOverrides = (iterationOverrides: IterationOverrides) => {
    const mockService = _.cloneDeep(this.props.rowData);
    mockService.dataSettings = getPublishConfig(iterationOverrides);
    this.props.mockServicePatchAction(mockService);
  };

  getShowLogComponent = () => {
    const showLogContent = (
      <>
        <Form.Label>{this.props.t('mockServiceDetails.log')}</Form.Label>
        <div style={{ paddingTop: 2 }}>
          {!this.props.disabled ? (
            <button
              className={'btn btn-link bth-no-hover btn-sm p-0 m-0'}
              id={`mockServiceDetailShowLogsLink` + this.props.rowData.id}
              onClick={event => {
                this.openLogModalHandler(event);
              }}
            >
              {this.props.t('mockServiceDetails.showLog')}
            </button>
          ) : (
            <span className="text-muted">{this.props.t('mockServiceDetails.showLog')}</span>
          )}
        </div>
      </>
    );

    //For public location, show "old" get log link
    return (
      <>
        {this.state.isModalWithLogsShown && (
          <MockServiceLogs closeModalHandler={this.closeLogsModalHandler} mockService={this.props.rowData} />
        )}
        {showLogContent}
      </>
    );
  };

  getExtendedInfoRow = () => {
    const { containerName, isDraft, id, serviceMockId, status } = this.props.rowData;
    const { t } = this.props;
    const mockVersion = this.props.rowData?.metadata?.version ? this.props.rowData.metadata.version : '';
    const secretsVaultAllowed = isFeatureEnabled('secretsPrivateVault') && isFeatureEnabled('enableSecrets');

    return (
      <Row className={'mb-4 mt-1'}>
        {secretsVaultAllowed && (
          <Col md={3}>
            <SecretsVaultDropdown
              id={this.state.secretsVaultId}
              secretsVaultList={this.props.secretsVaultList}
              selectedSecretsVaultId={this.state.secretsVaultId}
              onChangeSecretsVault={this.updateSecretsVaultId}
              disabled={this.props.disabled}
            />
          </Col>
        )}
        {/* Container Name */}
        <Col md={3}>
          <Form.Label className={'d-block'}>{t('mockServiceDetails.containerName')}</Form.Label>
          <span>{_.isNil(containerName) ? '-' : containerName}</span>
        </Col>

        {/* ID */}
        <Col md={2}>
          <Form.Label className={'d-block'}>{t('mockServiceDetails.id')}</Form.Label>
          <span>{isDraft ? (serviceMockId ? serviceMockId : '-') : id}</span>
        </Col>

        <Col md={4}>
          <Row>
            <Col md={9}>
              {/* Mock Version */}
              <Row>
                <Col md={4}>
                  <Form.Label>{this.props.t('mockServiceDetails.version')}</Form.Label>
                  <div className="mt-3">{!_.isEmpty(mockVersion) ? <span>{mockVersion}</span> : <span>-</span>}</div>
                </Col>
                {/* Deployment Info */}
                <Col md={4}>
                  {(status === 'RUNNING' || status === 'FAILED') && (
                    <>
                      <Form.Label>Deployment Timing</Form.Label>
                      <DeploymentInfo mockService={this.props.rowData} />
                    </>
                  )}
                </Col>
                {/* Last Used */}
                <Col md={4}>
                  <Form.Label>Last Used</Form.Label>
                  <div className="mt-3">
                    <span>
                      {this.props.rowData.lastUsedAt ? getDateWithFormat(this.props.rowData.lastUsedAt) : 'Never'}
                    </span>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  getShowMoreButton = () => {
    return (
      <div className={styles.showMoreBlock}>
        <Button
          className={clsx(styles.showMoreButton, 'btn-no-hover')}
          onClick={() => {
            this.props.toggleShowMore();
            MixpanelService.track(MOCK_SERVICE_SHOW_MORE());
          }}
          variant={'link'}
        >
          {this.props.showMore === true ? (
            <>
              {this.props.t('common:actions.showLess')}
              <BzmIcon className={clsx('ml-1', styles.chevronIcon)} variant={'icon-bzm-chevron-up'} />
            </>
          ) : (
            <>
              {this.props.t('common:actions.showMore')}
              <BzmIcon className={clsx('ml-1', styles.chevronIcon)} variant={'icon-bzm-chevron-down'} />
            </>
          )}
        </Button>
      </div>
    );
  };

  render() {
    const { t } = this.props;
    const createdMessage = dateAndAuthorFormat(this.props.rowData.created, this.props.rowData.createdBy);
    const updatedMessage = dateAndAuthorFormat(this.props.rowData.updated, this.props.rowData.updatedBy);
    const isLogLinkShown = this.props.rowData?.metaData?.isDeployed && this.props.rowData.type !== SV;

    return (
      <>
        {/* We do not support MAR Virtual Services anymore */}
        {this.props.rowData.type === 'SV' && (
          <Row>
            <Col md={12}>
              <MarEolAlert />
            </Col>
          </Row>
        )}

        <Row className={'mb-4'}>
          <Col md={3}>
            <Form.Label htmlFor={'mockServiceDetailDescriptionInput' + this.props.rowData.id}>
              {t('mockServiceDetails.description')}
            </Form.Label>
            <Form.Control
              id={'mockServiceDetailDescriptionInput' + this.props.rowData.id}
              name="description"
              autoComplete="off"
              value={this.state.description}
              onChange={(event: any) => this.updateLocalDescription(event.target.value)}
              readOnly={this.props.disabled}
            />
          </Col>
          <Col md={2}>
            <div>
              <span className={'form-label'}>
                <label htmlFor={'configuration-picker' + this.state.configurationId}>
                  {t('mockServiceDetails.configuration')}
                </label>
              </span>
              <span
                id="serviceHelp"
                title={this.props.t('common:showHelp')}
              >
                <HelpTooltip id={'mock-services-intro-help'} />
              </span>
            </div>
            <ConfigurationsDropDown
              id={this.state.configurationId}
              configurationsList={this.props.configurations}
              selectedConfigurationId={this.state.configurationId}
              onChangeConfiguration={this.updateConfigurationId}
              disabled={this.props.disabled}
            />
          </Col>
          <Col md={4}>
            <Row>
              <Col md={isLogLinkShown ? 9 : 10}>
                <Row>
                  {/* Created TS */}
                  <Col md={6}>
                    <Form.Label>{t('mockServiceDetails.created')}</Form.Label>
                    <div id={'mockServiceDetailCreatedValue' + this.props.rowData.id} className="mt-2">
                      <div className="mt-1 text-truncate">
                        <span title={createdMessage}> {createdMessage}</span>
                      </div>
                    </div>
                  </Col>
                  {/* ./ Created TS */}
                  {/* Updated TS */}
                  <Col md={6}>
                    <Form.Label>{t('mockServiceDetails.updated')}</Form.Label>
                    <div id={'mockServiceDetailUpdatedValue' + this.props.rowData.id} className="mt-2">
                      <div className="mt-1 text-truncate">
                        <span title={updatedMessage}> {updatedMessage}</span>
                      </div>
                    </div>
                  </Col>
                  {/* ./Updated TS */}
                </Row>
              </Col>
              <Col md={isLogLinkShown ? 3 : 2}>
                {/* Show Logs Button & Modal */}
                {isLogLinkShown && this.getShowLogComponent()}
                {/* ./ Show Logs Button & Modal */}
              </Col>
            </Row>
          </Col>

          {/* Show More*/}
          <Col md={1}>{this.getShowMoreButton()}</Col>
          {/* ./Show More*/}

          {/* Action Buttons */}
          <Col md={2}>
            <div style={{ minWidth: 100 }}>{this.getActionButtons()}</div>
          </Col>
          {/* ./Action Buttons */}
        </Row>

        {this.props.showMore === true && this.getExtendedInfoRow()}

        {/* Tabs */}
        <MockServiceDetailTabs
          mockService={this.props.rowData}
          changeActiveTabHandler={this.changeActiveTabHandler}
          activeTab={this.props.activeTab}
          disabled={this.props.disabled}
          updateParameters={(params: MockParameterState, debounce: boolean) => {
            if (debounce) {
              this.updateParametersDebounced(params);
            } else {
              this.updateParameters(params);
            }
          }}
          updateRunTimeConfig={this.updateRuntimeConfig}
          updateIterationOverrides={this.updateIterationOverrides}
        />
      </>
    );
  }
}

function mapStateToProps({
  blazeUserConfig,
  mockservices,
  mockServiceDetails,
  configurations,
  secretsVault,
}: ApplicationState) {
  return {
    blazeUserConfig: blazeUserConfig.blazeUserConfig,
    activeTab: mockservices.activeTab,
    assetBundle: mockServiceDetails.assets.assetBundle,
    showMore: mockServiceDetails.showMore,
    configurations: configurations.configurations,
    secretsVaultList: secretsVault.secretsVaultList,
    activeParamTab: mockservices.activeParamTab,
  };
}

const mapDispatchToProps = {
  mockServiceChanged,
  mockServiceTransactionsChanged,
  mockServiceTransactionContentChanged,
  mockServicePatchAction,
  discardLocalChanges,
  createDraft,
  mockServiceDetailTabChanged,
  showToastNotification,
  getAssetsForMockService,
  updateMockServiceAssets,
  mockServiceParameterTabChanged,
  toggleShowMore,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('mockServices')(connect(mapStateToProps, mapDispatchToProps)(MockServiceDetail));
